import { useState } from "react";

import ZentraqLogo from "./ZentraqLogo";
import ResizableTables from "./common/ResizableTables";
import ScrollToTopButton from "./common/ScrollToTopButton.tsx";

const primaryNavItems = [
  { key: "wareneingang", label: "Wareneingang", icon: "box" },
  { key: "warenausgang", label: "Warenausgang", icon: "box" },
];

const secondaryNavItems = [
  { key: "mehraufwand", label: "Mehraufwand", icon: "plus" },
  { key: "tarifrechner", label: "Tarifrechner", icon: "calculator" },
];

const adminNavItems = [
  { key: "dashboard", label: "Dashboard", icon: "home" },
];

const adminItems = [
  { key: "lieferanten", label: "Kunden", icon: "users" },
  { key: "benutzer", label: "Benutzer", icon: "user" },
  { key: "tarife", label: "Tarifverwaltung", icon: "file" },
  { key: "tarifimport", label: "Tarifimport", icon: "upload" },
  { key: "zuschlaege", label: "Zuschlagsverwaltung", icon: "percent" },
  { key: "abrechnung", label: "Abrechnung", icon: "calculator" },
  { key: "rechnungen", label: "Rechnungen", icon: "file" },
  { key: "abrechnungsdaten", label: "Abrechnungsdaten", icon: "file" },
  { key: "protokoll", label: "Protokoll", icon: "clipboard" },
];

function NavIcon({ name }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const paths = {
    home: <><path {...common} d="M3 11 12 4l9 7" /><path {...common} d="M5 10v10h14V10" /><path {...common} d="M10 20v-6h4v6" /></>,
    box: <><path {...common} d="M4 7h16v12H4z" /><path {...common} d="M8 7V5h8v2" /><path {...common} d="M8 12h8" /></>,
    leaf: <><path {...common} d="M20 4C10 4 5 9 5 19c10 0 15-5 15-15Z" /><path {...common} d="M5 19 15 9" /></>,
    shield: <><path {...common} d="M12 3 20 6v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z" /></>,
    plus: <><circle {...common} cx="12" cy="12" r="8" /><path {...common} d="M12 8v8M8 12h8" /></>,
    calculator: <><rect {...common} x="5" y="3" width="14" height="18" rx="2" /><path {...common} d="M8 7h8M8 11h2M12 11h2M16 11h0M8 15h2M12 15h2M16 15h0" /></>,
    users: <><path {...common} d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" /><path {...common} d="M2 21a7 7 0 0 1 14 0" /><path {...common} d="M17 11a3 3 0 0 0 0-6" /><path {...common} d="M18 21a5 5 0 0 0-3-4.5" /></>,
    user: <><circle {...common} cx="12" cy="8" r="4" /><path {...common} d="M4 21a8 8 0 0 1 16 0" /></>,
    file: <><path {...common} d="M6 3h9l3 3v15H6z" /><path {...common} d="M14 3v4h4M9 12h6M9 16h6" /></>,
    upload: <><path {...common} d="M12 4v12" /><path {...common} d="m7 9 5-5 5 5" /><path {...common} d="M5 20h14" /></>,
    percent: <><path {...common} d="m5 19 14-14" /><circle {...common} cx="7" cy="7" r="2" /><circle {...common} cx="17" cy="17" r="2" /></>,
    clipboard: <><path {...common} d="M8 4h8v4H8z" /><path {...common} d="M6 6H5v15h14V6h-1" /><path {...common} d="M8 13h8M8 17h6" /></>,
  };

  return <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

export default function Layout({
  user,
  onLogout,
  activePage,
  setActivePage,
  isDesktop = true,
  children,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mdeMenuOpen, setMdeMenuOpen] = useState(false);
  const effectiveCollapsed = isDesktop && collapsed;
  const permissionNavItems = [
    ...(user?.isAdmin || user?.bioApproval
      ? [{ key: "bio-we", label: "BIO WE", icon: "BIO" }]
      : []),
    ...(user?.isAdmin || user?.tracesApproval
      ? [{ key: "traces-we", label: "Traces WE", icon: "shield" }]
      : []),
  ];
  const groupedNavItems = [
    ...(user?.isAdmin && isDesktop
      ? [{ label: "UEBERSICHT", items: adminNavItems }]
      : []),
    { label: "ERFASSUNG", items: primaryNavItems },
    ...(permissionNavItems.find((item) => item.key === "bio-we")
      ? [{ label: "BIO", items: [permissionNavItems.find((item) => item.key === "bio-we")] }]
      : []),
    ...(permissionNavItems.find((item) => item.key === "traces-we")
      ? [{ label: "TRACES", items: [permissionNavItems.find((item) => item.key === "traces-we")] }]
      : []),
    { label: "ZUSATZ", items: secondaryNavItems.slice(0, 1) },
    { label: "TARIF", items: secondaryNavItems.slice(1) },
  ];
  const handleMdeNavClick = (pageKey) => {
    setActivePage(pageKey);
    setMdeMenuOpen(false);
  };

  return (
    <div className={`app-shell ${effectiveCollapsed ? "app-shell-collapsed" : "app-shell-open"}`}>
      <header className="topbar">
        {!isDesktop && (
          <div className="mde-menu-wrap">
            <button
              type="button"
              className="mde-menu-button"
              onClick={() => setMdeMenuOpen((current) => !current)}
              aria-label="Menue oeffnen"
              title="Menue"
            >
              ⋮
            </button>
            {mdeMenuOpen && (
              <div className="mde-menu-popover">
                {groupedNavItems.map((group) => (
                  <div className="mde-menu-section" key={group.label}>
                    <p className="mde-menu-label">{group.label}</p>
                    {group.items.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleMdeNavClick(item.key)}
                        className={`mde-menu-item ${
                          activePage === item.key ? "mde-menu-item-active" : ""
                        }`}
                      >
                        <span className="nav-meta">
                          <NavIcon name={item.icon === "BIO" ? "leaf" : item.icon} />
                        </span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                ))}

                {user?.isAdmin && (
                  <div className="mde-menu-section">
                    <p className="mde-menu-label">ADMIN</p>
                    {adminItems.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleMdeNavClick(item.key)}
                        className={`mde-menu-item ${
                          activePage === item.key ? "mde-menu-item-active" : ""
                        }`}
                      >
                        <span className="nav-meta"><NavIcon name={item.icon} /></span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="topbar-user desktop-user">
          <p className="section-text">Angemeldet als</p>
          <h2>{user?.username}</h2>
        </div>

        <div className="topbar-center">
          <ZentraqLogo className="brand-logo brand-logo-header" theme="light" />
        </div>

        {!isDesktop && (
          <div className="mde-topbar-user">
            <span>Angemeldet als</span>
            <strong>{user?.username}</strong>
          </div>
        )}

        <button onClick={onLogout} className="button button-danger">
          Logout
        </button>
      </header>

      <div className="mobile-userbar">
        <p className="section-text">Angemeldet als</p>
        <h2>{user?.username}</h2>
      </div>

      <div className="body-shell">
        <aside className={`sidebar ${effectiveCollapsed ? "sidebar-collapsed" : ""}`}>
          <div className="sidebar-brand">
            <div className="sidebar-brand-row">
              <ZentraqLogo
                className="brand-logo brand-logo-sidebar"
                variant={effectiveCollapsed ? "mark" : "full"}
                theme="dark"
              />
            </div>
          </div>

          {isDesktop && (
            <button
              className="sidebar-toggle"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Menue oeffnen" : "Menue einklappen"}
            >
              {collapsed ? ">>" : "<<"}
              {!collapsed && <span> Menue einklappen</span>}
            </button>
          )}

          <nav className="sidebar-nav">
            {groupedNavItems.map((group) => (
              <div className="nav-section" key={group.label}>
                {!effectiveCollapsed && <p className="eyebrow eyebrow-dark nav-section-label">{group.label}</p>}
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setActivePage(item.key)}
                    title={item.label}
                    className={`nav-button ${
                      activePage === item.key ? "nav-button-active" : ""
                    }`}
                  >
                    <span className="nav-meta"><NavIcon name={item.icon === "BIO" ? "leaf" : item.icon} /></span>
                    {!effectiveCollapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                ))}
              </div>
            ))}

            {user?.isAdmin && (
              <div className="nav-group">
                {!effectiveCollapsed && <p className="eyebrow eyebrow-dark nav-group-label">ADMIN</p>}
                {adminItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setActivePage(item.key)}
                    title={item.label}
                    className={`nav-button ${
                      activePage === item.key ? "nav-button-active" : ""
                    }`}
                  >
                    <span className="nav-meta"><NavIcon name={item.icon} /></span>
                    {!effectiveCollapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                ))}
              </div>
            )}
          </nav>
        </aside>

        <main className="main-shell">
          <div className="content-shell">{children}</div>
        </main>
      </div>
      <ResizableTables />
      <ScrollToTopButton />
    </div>
  );
}


